import React from 'react'
import PropTypes from 'prop-types'
import {Box, IconButton} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Mute, Pause, Play, Unmute} from '../icons'
import styles from './styles'

const DEFAULT_LABELS = {play: 'Play', pause: 'Pause', mute: 'Mute', unmute: 'Unmute'}

/**
 * Play/pause + mute toggles overlaid on a video. Positioned top-right of the nearest
 * `position: relative` ancestor. With `revealOnHover` the controls stay visible on
 * mobile and appear on hover/focus on desktop when an ancestor carries
 * `data-video-hover-group`.
 */
const VideoControls = ({
    isPlaying,
    isMuted,
    onTogglePlay,
    onToggleMute,
    showPlayPause = true,
    showMute = true,
    revealOnHover = false,
    labels,
    ...props
}) => {
    const hasPlayPause = showPlayPause && Boolean(onTogglePlay)
    const hasMute = showMute && Boolean(onToggleMute)
    if (!hasPlayPause && !hasMute) return null

    const text = {...DEFAULT_LABELS, ...labels}
    const {sx, ...container} = styles.container({revealOnHover})

    const renderIcon = (Icon) => (
        <Box {...styles.circle}>
            <Icon {...styles.icon} />
        </Box>
    )

    return (
        <Box data-testid="video-controls" {...container} {...props} sx={{...sx, ...props.sx}}>
            {hasPlayPause && (
                <IconButton
                    aria-label={isPlaying ? text.pause : text.play}
                    onClick={onTogglePlay}
                    icon={renderIcon(isPlaying ? Pause : Play)}
                    {...styles.button}
                />
            )}
            {hasMute && (
                <IconButton
                    aria-label={isMuted ? text.unmute : text.mute}
                    onClick={onToggleMute}
                    icon={renderIcon(isMuted ? Unmute : Mute)}
                    {...styles.button}
                />
            )}
        </Box>
    )
}

VideoControls.propTypes = {
    isPlaying: PropTypes.bool,
    isMuted: PropTypes.bool,
    onTogglePlay: PropTypes.func,
    onToggleMute: PropTypes.func,
    showPlayPause: PropTypes.bool,
    showMute: PropTypes.bool,
    revealOnHover: PropTypes.bool,
    labels: PropTypes.shape({
        play: PropTypes.string,
        pause: PropTypes.string,
        mute: PropTypes.string,
        unmute: PropTypes.string
    }),
    sx: PropTypes.object
}

export default VideoControls
